// @ts-check
// Seletor de modelo do WhatsApp agrupado como a escola pensa (contrato 09 de campanhas de WhatsApp, seção 2; regra
// 3.1.7 da spec de WhatsApp): Marketing, Comunicados e Atendimento na ordem do lugar (campanha de marketing,
// comunicado, fluxo ou conversa), grupos recolhidos quando a regra manda, selos de qualidade e do botão de saída, e os
// atalhos "Criar modelo" e "Atualizar lista". Um `listbox` só, com os cabeçalhos dos grupos como apresentação.
// Usado pela campanha (2B) e pelo nó do fluxo (2D); a conversa usa os mesmos grupos no TemplatePicker.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { Button } from '../../ui/button.js';
import { Loader } from '../../ui/feedback.js';
import { navigate } from '../../app/router.js';
import { templateGroupsFor } from '../../../../shared/whatsapp/template-rules.js';
import { TemplateBadges } from '../settings/whatsapp-templates/whatsapp-template-card.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/integrations.js').WhatsappTemplate} WhatsappTemplate */

// no seletor só os selos que mudam a escolha: qualidade baixa e o botão de saída
const PICKER_BADGE_KEYS = new Set(['quality.RED', 'badges.optOutButton', 'category.willChange', 'category.willChangeSoon']);

/**
 * Move o foco entre as opções habilitadas com as setas, Home e End.
 * @param {KeyboardEvent} event
 */
function moveOptionFocus(event) {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
  const container = /** @type {HTMLElement} */ (event.currentTarget);
  const options = /** @type {HTMLElement[]} */ ([...container.querySelectorAll('[role="option"]:not([aria-disabled="true"])')]);
  if (options.length === 0) return;
  event.preventDefault();
  const index = options.indexOf(/** @type {HTMLElement} */ (document.activeElement));
  const next = event.key === 'Home' ? 0
    : event.key === 'End' ? options.length - 1
      : event.key === 'ArrowDown' ? Math.min(index + 1, options.length - 1) : Math.max(index - 1, 0);
  options[next]?.focus();
}

/**
 * @param {{ templates: WhatsappTemplate[] | null, context: 'MARKETING_CAMPAIGN' | 'ANNOUNCEMENT' | 'FLOW' | 'CONVERSATION', selectedId: string | null,
 *   onSelect: (template: WhatsappTemplate) => void, disabledReason?: (template: WhatsappTemplate) => string | null, label?: string,
 *   createHref?: string | null, onRefresh?: (() => void) | null, isRefreshing?: boolean, emptyText?: string }} props
 *   disabledReason: texto quando o modelo não pode ser escolhido ali (por exemplo, marketing sem autorização)
 */
export function WhatsappTemplateGroupedPicker({
  templates, context, selectedId, onSelect, disabledReason = () => null, label, createHref = null, onRefresh = null, isRefreshing = false, emptyText,
}) {
  const groups = templateGroupsFor(templates ?? [], context);
  const [openGroups, setOpenGroups] = useState(/** @type {Record<string, boolean>} */ ({}));
  const footer = createHref || onRefresh
    ? html`<div class="wa-picker-footer">
        ${createHref
          ? html`<a class="integration-link template-shortcut-link" href=${createHref} onClick=${(/** @type {MouseEvent} */ event) => {
              if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              event.preventDefault();
              navigate(createHref);
            }}><${Icon} name="IconPlus" size=${14} />${t('whatsappTemplates.groupedPicker.createTemplate')}</a>`
          : null}
        ${onRefresh ? html`<${Button} title=${t('whatsappTemplates.groupedPicker.refresh')} icon="IconRefresh" variant="tertiary" size="small" isLoading=${isRefreshing} onClick=${onRefresh} />` : null}
      </div>`
    : null;
  if (templates === null) return html`<${Loader} />`;
  if (groups.every((group) => group.templates.length === 0)) {
    return html`<div class="wa-picker-wrapper"><p class="wa-picker-empty">${emptyText ?? t('whatsappTemplates.groupedPicker.empty')}</p>${footer}</div>`;
  }
  return html`<div class="wa-picker-wrapper">
    <div class="wa-picker" role="listbox" aria-label=${label ?? t('whatsappTemplates.groupedPicker.label')} onKeyDown=${moveOptionFocus}>
      ${groups.map((group) => {
        const isOpen = openGroups[group.key] ?? (!group.isCollapsed || group.templates.some((template) => template.id === selectedId));
        const groupName = t(`whatsappTemplates.groups.${group.key}`);
        return html`<div key=${group.key} role="presentation" class="wa-picker-group">
          <div class="wa-picker-group-header" role="presentation">
            ${group.isCollapsed
              ? html`<button type="button" class="wa-picker-group-toggle" aria-expanded=${isOpen ? 'true' : 'false'}
                  onClick=${() => setOpenGroups({ ...openGroups, [group.key]: !isOpen })}>
                  <${Icon} name=${isOpen ? 'IconChevronDown' : 'IconChevronRight'} size=${12} />
                  ${t('whatsappTemplates.groupedPicker.collapsedGroup', { group: groupName, count: group.templates.length })}
                </button>`
              : html`<span>${groupName}</span>`}
          </div>
          ${!isOpen
            ? null
            : group.templates.length === 0
              ? html`<p class="wa-picker-empty" role="presentation">${t('whatsappTemplates.groupedPicker.empty')}</p>`
              : group.templates.map((template) => {
                  const reason = disabledReason(template);
                  const isSelected = template.id === selectedId;
                  return html`<button key=${template.id} type="button" role="option" class="wa-picker-option" aria-selected=${isSelected ? 'true' : 'false'}
                    aria-disabled=${reason ? 'true' : undefined} tabindex=${isSelected ? 0 : -1}
                    onClick=${() => {
                      if (!reason) onSelect(template);
                    }}>
                    <span class="wa-picker-option-name">
                      ${template.name}
                    </span>
                    <${TemplateBadges} template=${template} only=${PICKER_BADGE_KEYS} inline=${true} />
                    <span class="wa-picker-option-body">${template.bodyText}</span>
                    ${reason
                      ? html`<span class="wa-picker-option-note">${reason}</span>`
                      : group.key === 'MARKETING' && (context === 'FLOW' || context === 'CONVERSATION')
                        ? html`<span class="wa-picker-option-note">${t('whatsappTemplates.groupedPicker.marketingNote')}</span>`
                        : null}
                  </button>`;
                })}
        </div>`;
      })}
    </div>
    ${footer}
  </div>`;
}
